import { ADVERT_FORMAT_TYPES } from '@web-platform/shared-universal-constants/src/adverts';

import type { SkyscannerAdvertsAdsretrievalserviceV1alpha1Response as AdsCapabilityResponse } from '@skyscanner-internal/ads-retrieval-service-web-http-client';
import type {
  FilteredExploreAirline,
  FilteredExploreAirlineById,
  FilteredExploreAirlineCreative,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/filteredExploreAirline';

type GetAdsResponse = AdsCapabilityResponse.GetAdsResponse;
type Advert = AdsCapabilityResponse.Advert;

/**
 * Get tracking pixels with safe defaults
 *
 * @param {AdsCapabilityResponse.TrackingPixels} pixels - Tracking pixels from API response
 * @returns {object} Tracking pixels object with impression array
 */
const getPixels = (pixels?: AdsCapabilityResponse.TrackingPixels) => ({
  ...pixels,
  impression: pixels?.impression ?? [],
});

/**
 * Transform ARS v1alpha1 response to FilteredExploreAirline Redux state
 *
 * @param {GetAdsResponse | null | undefined} response - The ARS v1alpha1 API response
 * @returns {FilteredExploreAirlineById} Map of creative IDs to FilteredExploreAirline objects
 */
export const transformAdsCapabilityResponseToFilteredExploreAirline = (
  response: GetAdsResponse | null | undefined,
): FilteredExploreAirlineById => {
  if (!response?.slotAdverts) {
    return {};
  }

  const byId: FilteredExploreAirlineById = {};

  Object.values(response.slotAdverts).forEach((adverts) => {
    (adverts.items ?? []).forEach((advert: Advert) => {
      const { creative, trackingContext } = advert;

      const filteredExploreAirlineCreative = creative?.filteredExploreAirline;

      if (!filteredExploreAirlineCreative) {
        return;
      }

      const idCreative = trackingContext?.idCreative;
      const { heroImage, partnerId, partnerLogo, partnerName, titleText } =
        filteredExploreAirlineCreative;

      if (!idCreative || !heroImage || !partnerLogo || !partnerName || !titleText || !partnerId) {
        return;
      }

      const creativeData: FilteredExploreAirlineCreative = {
        heroImage,
        partnerLogo,
        mobilePartnerLogo: filteredExploreAirlineCreative.mobilePartnerLogo,
        partnerName,
        titleText,
        subtitleText: filteredExploreAirlineCreative.subtitleText,
        pioPartnerName: filteredExploreAirlineCreative.partnerBillingInfo?.pioPartnerName,
        pioAgencyName: filteredExploreAirlineCreative.partnerBillingInfo?.pioAgencyName,
        partnerId,
      };

      const filteredExploreAirline: FilteredExploreAirline = {
        pixels: getPixels(advert.trackingPixels),
        analyticsProperties: {
          idCreative,
          idFormat: ADVERT_FORMAT_TYPES.FILTERED_EXPLORE_AIRLINE,
          idPlacement: trackingContext.idPlacement ?? '',
          idTracking: trackingContext.idTracking ?? '',
        },
        creative: creativeData,
        performanceEventsPayloads: advert.performanceEventsPayloads,
      };

      byId[idCreative] = filteredExploreAirline;
    });
  });

  return byId;
};
